import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../../components/common'
import api from '../../services/api'

interface Segnalazione {
  id: number
  nomeLocale: string
  tipoLocale?: string
  indirizzoApprossimativo?: string
  comune?: string
  provincia?: string
  descrizione?: string
  specialitaSuggerite?: string
  comeScoperto?: string
  emailContatto?: string
  stato: string
  noteInterne?: string
  createdAt: string
  utente?: { nome: string; email: string }
  valutatore?: { nome: string }
  valutataIl?: string
  localeCreato?: { id: number; slug: string; nome: string }
}

/**
 * Pagina gestione segnalazioni admin
 */
export default function AdminSegnalazioni() {
  const navigate = useNavigate()
  const [segnalazioni, setSegnalazioni] = useState<Segnalazione[]>([])
  const [loading, setLoading] = useState(true)
  const [filtroStato, setFiltroStato] = useState<string>('')

  useEffect(() => {
    fetchSegnalazioni()
  }, [filtroStato])

  const fetchSegnalazioni = async () => {
    try {
      // L'API admin restituisce un array diretto
      const query = filtroStato ? `?stato=${filtroStato}` : ''
      const data = await api.get<Segnalazione[]>(`/admin/segnalazioni${query}`)
      setSegnalazioni(data ?? [])
    } catch (error) {
      console.error('Errore caricamento segnalazioni:', error)
    } finally {
      setLoading(false)
    }
  }

  const updateStato = async (id: number, stato: string) => {
    try {
      await api.patch(`/admin/segnalazioni/${id}`, { stato })
      setSegnalazioni(segnalazioni.map(s =>
        s.id === id ? { ...s, stato } : s
      ))
    } catch (error) {
      console.error('Errore aggiornamento segnalazione:', error)
    }
  }

  const getStatoBadge = (stato: string) => {
    const colors: Record<string, string> = {
      nuova: 'bg-blue-500/20 text-blue-400',
      in_valutazione: 'bg-yellow-500/20 text-yellow-400',
      approvata: 'bg-green-500/20 text-green-400',
      rifiutata: 'bg-red-500/20 text-red-400',
      duplicata: 'bg-gray-500/20 text-gray-400',
    }
    return colors[stato] || 'bg-gray-500/20 text-gray-400'
  }

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <div key={i} className="bg-night-light border border-gray-800 rounded-xl p-4 animate-pulse">
            <div className="h-6 w-48 bg-gray-700 rounded mb-2"></div>
            <div className="h-4 w-32 bg-gray-700 rounded"></div>
          </div>
        ))}
      </div>
    )
  }

  if (!segnalazioni?.length) {
    return (
      <div className="bg-night-light border border-gray-800 rounded-xl p-8 text-center">
        <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
        </div>
        <h3 className="text-lg text-white mb-2">Nessuna segnalazione</h3>
        <p className="text-gray-400">Non ci sono segnalazioni da gestire al momento</p>
      </div>
    )
  }

  // Naviga al form di creazione locale pre-compilato con i dati della segnalazione
  const handleApprova = (segnalazione: Segnalazione) => {
    navigate(`/admin/locali/nuovo?segnalazioneId=${segnalazione.id}`)
  }

  const stati = [
    { value: '', label: 'Tutte' },
    { value: 'nuova', label: 'Nuove' },
    { value: 'in_valutazione', label: 'In valutazione' },
    { value: 'approvata', label: 'Approvate' },
    { value: 'rifiutata', label: 'Rifiutate' },
    { value: 'duplicata', label: 'Duplicate' },
  ]

  return (
    <div className="space-y-4">
      {/* Filtro per stato */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        {stati.map(s => (
          <button
            key={s.value}
            onClick={() => setFiltroStato(s.value)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filtroStato === s.value
                ? 'bg-gold text-black'
                : 'bg-night-light border border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <p className="text-gray-400 mb-4">{segnalazioni?.length ?? 0} segnalazioni</p>

      {(segnalazioni || []).map(segnalazione => (
        <div
          key={segnalazione.id}
          className="bg-night-light border border-gray-800 rounded-xl p-6"
        >
          {/* Header: nome + badge stato */}
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg text-white font-medium">{segnalazione.nomeLocale}</h3>
              <p className="text-sm text-gray-400">
                {segnalazione.tipoLocale && <span className="capitalize">{segnalazione.tipoLocale}</span>}
                {segnalazione.comune && ` • ${segnalazione.comune}`}
                {segnalazione.provincia && `, ${segnalazione.provincia}`}
              </p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatoBadge(segnalazione.stato)}`}>
              {segnalazione.stato.replace('_', ' ')}
            </span>
          </div>

          {/* Dettagli segnalazione */}
          {segnalazione.indirizzoApprossimativo && (
            <p className="text-sm text-gray-400 mb-2">
              <span className="text-gray-500">Indirizzo:</span> {segnalazione.indirizzoApprossimativo}
            </p>
          )}

          {segnalazione.descrizione && (
            <p className="text-gray-300 mb-3">{segnalazione.descrizione}</p>
          )}

          {segnalazione.specialitaSuggerite && (
            <p className="text-sm text-gray-400 mb-2">
              <span className="text-gray-500">Specialità:</span> {segnalazione.specialitaSuggerite}
            </p>
          )}

          {segnalazione.comeScoperto && (
            <p className="text-sm text-gray-400 mb-2">
              <span className="text-gray-500">Come scoperto:</span> {segnalazione.comeScoperto}
            </p>
          )}

          {/* Locale creato (se approvata) */}
          {segnalazione.localeCreato && (
            <div className="mt-3 mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-400">
                Locale creato:{' '}
                <button
                  onClick={() => navigate(`/admin/locali/${segnalazione.localeCreato!.id}`)}
                  className="underline hover:text-green-300"
                >
                  {segnalazione.localeCreato.nome}
                </button>
              </p>
            </div>
          )}

          {/* Note interne */}
          {segnalazione.noteInterne && (
            <div className="mt-3 mb-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-sm text-yellow-400">
                <span className="font-medium">Note interne:</span> {segnalazione.noteInterne}
              </p>
            </div>
          )}

          {/* Footer: info + azioni */}
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-800">
            <div className="text-sm text-gray-500">
              {segnalazione.utente ? (
                <span>{segnalazione.utente.nome} ({segnalazione.utente.email})</span>
              ) : (
                <span>Anonimo{segnalazione.emailContatto && ` (${segnalazione.emailContatto})`}</span>
              )}
              {' • '}
              {new Date(segnalazione.createdAt).toLocaleDateString('it-IT')}
              {segnalazione.valutatore && segnalazione.valutataIl && (
                <span className="text-gray-600">
                  {' • '}Valutata da {segnalazione.valutatore.nome} il{' '}
                  {new Date(segnalazione.valutataIl).toLocaleDateString('it-IT')}
                </span>
              )}
            </div>

            {/* Bottoni azione per stati non finali */}
            {(segnalazione.stato === 'nuova' || segnalazione.stato === 'in_valutazione') && (
              <div className="flex gap-2 flex-shrink-0">
                {segnalazione.stato === 'nuova' && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => updateStato(segnalazione.id, 'in_valutazione')}
                  >
                    In Valutazione
                  </Button>
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleApprova(segnalazione)}
                >
                  Approva e Crea Locale
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => updateStato(segnalazione.id, 'rifiutata')}
                >
                  Rifiuta
                </Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
